/**
 * Class Detail Page
 * Shows comprehensive information about a specific class for teachers
 * Includes learners, assignments, performance metrics, and quick actions
 */


import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Breadcrumbs, Link as MuiLink,
  CircularProgress, Alert, Card, CardContent, Chip, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, Stack, Avatar, Dialog, DialogTitle,
  DialogContent, DialogActions, List, ListItem, ListItemButton,
  ListItemText, Checkbox
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LinkIcon from '@mui/icons-material/Link';
import MenuBookIcon from '@mui/icons-material/MenuBook';

interface SchoolClass {
  id: number;
  name: string;
  subject: string;
  description: string;
  grade_level: number | null;
  adam_class_id: number | null;
  teacher: {
    id: number;
    first_name: string;
    last_name: string;
  };
}

interface Learner {
  id: number;
  first_name: string;
  last_name: string;
  student_id: string;
  submission_count?: number;
  average_score?: number;
  last_submission?: string;
}

interface Assignment {
  id: number;
  title: string;
  description: string;
  due_date: string;
  max_points: number;
  is_active: boolean;
  submission_count?: number;
  total_learners?: number;
  average_score?: number;
}

const ClassDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [classData, setClassData] = useState<SchoolClass | null>(null);
  const [learners, setLearners] = useState<Learner[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [availableAssignments, setAvailableAssignments] = useState<Assignment[]>([]);
  const [selectedAssignments, setSelectedAssignments] = useState<number[]>([]);
  const [linking, setLinking] = useState(false);

  const fetchClassData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch class details and enrollments in parallel
      const [classRes, enrollmentsRes] = await Promise.all([
        axios.get(`/api/school-classes/${id}/`),
        axios.get(`/api/school-classes/${id}/enrollments/`)
      ]);

      setClassData(classRes.data);

      // Extract learners from enrollments
      // The API returns: { class_id, class_name, total_learners, learners: [...] }
      const learnersArray = enrollmentsRes.data.learners || [];

      const learnersData = learnersArray.map((learner: any) => ({
        id: learner.id,
        first_name: learner.first_name,
        last_name: learner.last_name,
        student_id: learner.student_id,
        submission_count: 0, // Will be populated if we add this data
        average_score: null,
        last_submission: null
      }));

      setLearners(learnersData);

      // Fetch assignments for this class
      try {
        const assignmentsRes = await axios.get('/api/assignments/', {
          params: { page_size: 100 }
        });

        // Filter assignments that include this class
        const classAssignments = assignmentsRes.data.results.filter((assignment: any) =>
          assignment.assigned_classes_display?.some((c: any) => c.id === parseInt(id!))
        );

        setAssignments(classAssignments);
      } catch (err) {
        console.error('Error fetching assignments:', err);
        setAssignments([]);
      }

    } catch (err: any) {
      console.error('Error fetching class data:', err);
      setError(err.response?.data?.error || 'Failed to load class details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchClassData();
    }
  }, [id, fetchClassData]);

  const fetchAvailableAssignments = async () => {
    try {
      const response = await axios.get('/api/assignments/', {
        params: { page_size: 100 }
      });

      // Filter out assignments already linked to this class
      const currentAssignmentIds = assignments.map(a => a.id);
      const available = response.data.results.filter(
        (assignment: Assignment) => !currentAssignmentIds.includes(assignment.id)
      );

      setAvailableAssignments(available);
    } catch (err) {
      console.error('Error fetching available assignments:', err);
      setAvailableAssignments([]);
    }
  };

  const handleToggleAssignment = (assignmentId: number) => {
    setSelectedAssignments(prev => {
      if (prev.includes(assignmentId)) {
        return prev.filter(id => id !== assignmentId);
      } else {
        return [...prev, assignmentId];
      }
    });
  };

  const handleLinkAssignments = async () => {
    if (selectedAssignments.length === 0) {
      return;
    }

    try {
      setLinking(true);

      // Link each selected assignment to this class
      for (const assignmentId of selectedAssignments) {
        // We need to fetch the assignment first to get its current assigned_classes
        const assignmentRes = await axios.get(`/api/assignments/${assignmentId}/`);
        const assignment = assignmentRes.data;

        // Add this class to the assigned_classes array if not already present
        const currentClassIds = assignment.assigned_classes?.map((c: any) => c.id) || [];
        if (!currentClassIds.includes(parseInt(id!))) {
          const updatedClassIds = [...currentClassIds, parseInt(id!)];

          // Update the assignment with the new class list
          await axios.patch(`/api/assignments/${assignmentId}/`, {
            assigned_classes: updatedClassIds
          });
        }
      }

      // Refresh the class data to show newly linked assignments
      await fetchClassData();

      // Close dialog and reset state
      setLinkDialogOpen(false);
      setSelectedAssignments([]);
      setAvailableAssignments([]);

    } catch (err: any) {
      console.error('Error linking assignments:', err);
      alert(err.response?.data?.error || 'Failed to link assignments');
    } finally {
      setLinking(false);
    }
  };

  const filteredLearners = learners.filter(learner => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      learner.first_name.toLowerCase().includes(query) ||
      learner.last_name.toLowerCase().includes(query) ||
      learner.student_id.toLowerCase().includes(query)
    );
  });

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getAssignmentStatus = (assignment: Assignment) => {
    const now = new Date();
    const dueDate = new Date(assignment.due_date);

    if (!assignment.is_active) {
      return { label: 'Inactive', color: 'default' as const };
    } else if (dueDate < now) {
      return { label: 'Past Due', color: 'error' as const };
    } else if (dueDate.getTime() - now.getTime() < 7 * 24 * 60 * 60 * 1000) {
      return { label: 'Due Soon', color: 'warning' as const };
    } else {
      return { label: 'Active', color: 'success' as const };
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !classData) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Class not found'}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <HomeIcon fontSize="small" />
          Dashboard
        </MuiLink>
        <Typography color="text.primary">{classData.name}</Typography>
      </Breadcrumbs>

      {/* Class Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h4" gutterBottom>
              {classData.name}
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {classData.subject}
            </Typography>
            {classData.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                {classData.description}
              </Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              {classData.grade_level && (
                <Chip label={`Grade ${classData.grade_level}`} color="primary" />
              )}
              {classData.adam_class_id && (
                <Chip label="ADAM Synced" color="success" variant="outlined" />
              )}
              <Chip label={`${learners.length} Learners`} icon={<PeopleIcon />} />
              <Chip label={`${assignments.length} Assignments`} icon={<AssignmentIcon />} />
            </Stack>
          </Box>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/classes/${id}/edit`)}
          >
            Edit Class
          </Button>
        </Box>
      </Paper>

      {/* Quick Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<PersonAddIcon />}
            onClick={() => navigate(`/classes/${id}/enrollment`)}
          >
            Manage Enrollment
          </Button>
          <Button
            variant="contained"
            fullWidth
            startIcon={<AddIcon />}
            onClick={() => navigate('/assignments/new')}
          >
            Create Assignment
          </Button>
          <Button
            variant="contained"
            fullWidth
            startIcon={<CloudUploadIcon />}
            onClick={() => navigate('/fast-mark')}
          >
            Fast Mark
          </Button>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<VisibilityIcon />}
            onClick={() => navigate(`/submissions?class=${id}`)}
          >
            View Submissions
          </Button>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<MenuBookIcon />}
            onClick={() => navigate(`/classes/${id}/content`)}
          >
            View Class Content
          </Button>
        </Box>
      </Paper>

      {/* Main Content - Learners and Assignments */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.4fr 1fr' }, gap: 3 }}>
        {/* Enrolled Learners Section */}
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" display="flex" alignItems="center">
              <PeopleIcon sx={{ mr: 1 }} />
              Enrolled Learners ({filteredLearners.length})
            </Typography>
            <TextField
              size="small"
              placeholder="Search learners..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
          </Box>

          {filteredLearners.length === 0 ? (
            <Alert severity="info">
              {searchQuery ? 'No learners match your search.' : 'No learners enrolled in this class yet.'}
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Learner</TableCell>
                    <TableCell>Student ID</TableCell>
                    <TableCell align="center">Submissions</TableCell>
                    <TableCell align="center">Avg Score</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLearners.map((learner) => (
                    <TableRow key={learner.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                            {getInitials(learner.first_name, learner.last_name)}
                          </Avatar>
                          <Typography variant="body2">
                            {learner.first_name} {learner.last_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{learner.student_id}</TableCell>
                      <TableCell align="center">
                        {learner.submission_count || 0}
                      </TableCell>
                      <TableCell align="center">
                        {learner.average_score ? `${learner.average_score}%` : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => navigate(`/submissions?learner=${learner.id}`)}
                        >
                          View Work
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Assignments Section */}
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" display="flex" alignItems="center">
              <AssignmentIcon sx={{ mr: 1 }} />
              Assignments ({assignments.length})
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<LinkIcon />}
                onClick={() => {
                  setLinkDialogOpen(true);
                  fetchAvailableAssignments();
                }}
              >
                Link Existing
              </Button>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => navigate('/assignments/new')}
              >
                New
              </Button>
            </Box>
          </Box>

          {assignments.length === 0 ? (
            <Alert severity="info">
              No assignments for this class yet. Create one to get started!
            </Alert>
          ) : (
            <Stack spacing={2}>
              {assignments.map((assignment) => {
                const status = getAssignmentStatus(assignment);
                return (
                  <Card
                    key={assignment.id}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 3 }
                    }}
                    onClick={() => navigate(`/submissions?assignment=${assignment.id}`)}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography variant="subtitle1" sx={{ flexGrow: 1, mr: 1 }}>
                          {assignment.title}
                        </Typography>
                        <Chip label={status.label} color={status.color} size="small" />
                      </Box>

                      {assignment.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {assignment.description}
                        </Typography>
                      )}

                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          Due: {formatDate(assignment.due_date)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {assignment.max_points} points
                        </Typography>
                      </Box>

                      {assignment.submission_count !== undefined && assignment.total_learners && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {assignment.submission_count}/{assignment.total_learners} submitted
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Paper>
      </Box>

      {/* Link Existing Assignment Dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={() => {
          setLinkDialogOpen(false);
          setSelectedAssignments([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Link Existing Assignments to {classData.name}</DialogTitle>
        <DialogContent>
          {availableAssignments.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No available assignments to link. All assignments are already linked to this class, or you can create a new assignment.
            </Alert>
          ) : (
            <List sx={{ mt: 1 }}>
              {availableAssignments.map((assignment) => (
                <ListItem key={assignment.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleToggleAssignment(assignment.id)}
                    dense
                  >
                    <Checkbox
                      checked={selectedAssignments.includes(assignment.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText
                      primary={assignment.title}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {assignment.description || 'No description'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Due: {formatDate(assignment.due_date)} • {assignment.max_points} points
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setLinkDialogOpen(false);
              setSelectedAssignments([]);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLinkAssignments}
            variant="contained"
            disabled={selectedAssignments.length === 0 || linking}
          >
            {linking ? 'Linking...' : `Link ${selectedAssignments.length} Assignment${selectedAssignments.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClassDetailPage;
